import { createTheme } from '@mui/material/styles'

// Custom professional space theme for Black Hole Explorer
const spaceTheme = createTheme({
  palette: {
    mode: 'dark',
    primary: {
      main: '#1A2027', // Deep space blue
      light: '#2D3748',
      dark: '#0F1419',
      contrastText: '#ffffff',
    },
    secondary: {
      main: '#9c27b0', // Vibrant purple
      light: '#BA68C8',
      dark: '#7B1FA2',
      contrastText: '#ffffff',
    },
    background: {
      default: '#00001a',
      paper: 'rgba(26, 32, 39, 0.95)',
    },
    text: {
      primary: '#E2E8F0',
      secondary: '#A0AEC0',
    },
    success: {
      main: '#00ff88',
      light: '#66ffaa',
      dark: '#00cc6a',
    },
    warning: {
      main: '#ff6600',
      light: '#ff8833',
      dark: '#cc4400',
    },
    error: {
      main: '#ff1744',
      light: '#ff5983',
      dark: '#c4001d',
    },
    info: {
      main: '#00ffff',
      light: '#33ffff',
      dark: '#0099cc',
    },
  },
  typography: {
    fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
    h1: {
      fontFamily: '"Orbitron", "Roboto", sans-serif',
      fontSize: '3.5rem',
      fontWeight: 700,
      color: '#00ffff',
      textShadow: '0 0 20px rgba(0, 255, 255, 0.5)',
      letterSpacing: '0.1em',
    },
    h2: {
      fontFamily: '"Orbitron", "Roboto", sans-serif',
      fontSize: '2.5rem',
      fontWeight: 600,
      color: '#ff6600',
      textShadow: '0 0 15px rgba(255, 102, 0, 0.4)',
      letterSpacing: '0.05em',
    },
    h3: {
      fontFamily: '"Orbitron", "Roboto", sans-serif',
      fontSize: '2rem',
      fontWeight: 500,
      color: '#9c27b0',
      textShadow: '0 0 10px rgba(156, 39, 176, 0.4)',
      letterSpacing: '0.03em',
    },
    h4: {
      fontFamily: '"Orbitron", "Roboto", sans-serif',
      fontSize: '1.5rem',
      fontWeight: 500,
      color: '#00ff88',
      letterSpacing: '0.02em',
    },
    h5: {
      fontFamily: '"Orbitron", "Roboto", sans-serif',
      fontSize: '1.25rem',
      fontWeight: 500,
      color: '#ffffff',
    },
    h6: {
      fontFamily: '"Orbitron", "Roboto", sans-serif',
      fontSize: '1rem',
      fontWeight: 500,
      color: '#ffffff',
    },
    body1: {
      fontSize: '1rem',
      lineHeight: 1.6,
      color: '#E2E8F0',
    },
    body2: {
      fontSize: '0.875rem',
      lineHeight: 1.5,
      color: '#A0AEC0',
    },
    button: {
      fontFamily: '"Orbitron", "Roboto", sans-serif',
      fontWeight: 600,
      textTransform: 'none',
      letterSpacing: '0.05em',
    },
  },
  components: {
    MuiAppBar: {
      styleOverrides: {
        root: {
          backgroundColor: 'rgba(10, 10, 10, 0.95)',
          backdropFilter: 'blur(20px)',
          borderBottom: '1px solid rgba(0, 255, 255, 0.2)',
          boxShadow: '0 4px 20px rgba(0, 255, 255, 0.1)',
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          backgroundColor: 'rgba(26, 32, 39, 0.9)',
          backdropFilter: 'blur(10px)',
          border: '1px solid rgba(0, 255, 255, 0.1)',
          boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)',
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: '8px',
          padding: '10px 24px',
          transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
          '&:hover': {
            transform: 'translateY(-2px)',
            boxShadow: '0 8px 25px rgba(0, 255, 255, 0.3)',
          },
        },
        contained: {
          background: 'linear-gradient(45deg, #00ffff, #0099cc)',
          boxShadow: '0 4px 15px rgba(0, 255, 255, 0.2)',
          '&:hover': {
            background: 'linear-gradient(45deg, #33ffff, #00bbff)',
          },
        },
        outlined: {
          borderColor: '#9c27b0',
          color: '#9c27b0',
          '&:hover': {
            borderColor: '#BA68C8',
            backgroundColor: 'rgba(156, 39, 176, 0.1)',
          },
        },
      },
    },
    MuiSlider: {
      styleOverrides: {
        root: {
          '& .MuiSlider-thumb': {
            boxShadow: '0 0 15px rgba(0, 255, 255, 0.5)',
            '&:hover': {
              boxShadow: '0 0 25px rgba(0, 255, 255, 0.8)',
            },
          },
          '& .MuiSlider-track': {
            background: 'linear-gradient(90deg, #00ffff, #0099cc)',
          },
          '& .MuiSlider-rail': {
            backgroundColor: 'rgba(255, 255, 255, 0.1)',
          },
        },
      },
    },
    MuiSwitch: {
      styleOverrides: {
        root: {
          '& .MuiSwitch-switchBase.Mui-checked': {
            color: '#00ffff',
          },
          '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
            backgroundColor: '#00ffff',
          },
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          backgroundColor: 'rgba(0, 255, 255, 0.1)',
          border: '1px solid rgba(0, 255, 255, 0.3)',
          color: '#00ffff',
          '&:hover': {
            backgroundColor: 'rgba(0, 255, 255, 0.2)',
          },
        },
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            '& fieldset': {
              borderColor: 'rgba(0, 255, 255, 0.3)',
            },
            '&:hover fieldset': {
              borderColor: 'rgba(0, 255, 255, 0.5)',
            },
            '&.Mui-focused fieldset': {
              borderColor: '#00ffff',
              boxShadow: '0 0 15px rgba(0, 255, 255, 0.2)',
            },
          },
        },
      },
    },
  },
  shape: {
    borderRadius: 12,
  },
  spacing: 8,
})

export default spaceTheme
